// inv/src/app/app-components/deals-modules/AmountRange.jsx
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { InputNumber } from "../../../components/ui/inputNumber";
import { Button } from '../../../components/ui/button';
import { fetchInvestmentAmountRange } from './apiService';

const AmountRange = ({ selectedAmountFrom, selectedAmountTo, onAmountChange }) => {
  const { t } = useTranslation();
  const [amountRange, setAmountRange] = useState({ min: 0, max: 100000000 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [amountFromInput, setAmountFromInput] = useState('');
  const [amountToInput, setAmountToInput] = useState('');

  // Fetch the amount range when the component mounts
  useEffect(() => {
    const getAmountRange = async () => {
      try {
        const range = await fetchInvestmentAmountRange();
        setAmountRange({
          min: range.min_am_usd,
          max: range.max_am_usd,
        });
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    getAmountRange();
  }, []);

  const parseAmount = (input) => {
    if (!input) return null;
    const inputStr = typeof input === 'string' ? input : String(input);
    const regex = /^([\d,.]+)\s*([kKmMbBtT])?$/;
    const match = inputStr.trim().match(regex);

    if (match) {
      let value = parseFloat(match[1].replace(/,/g, ''));
      const multiplier = match[2] ? match[2].toLowerCase() : null;

      switch (multiplier) {
        case 'k':
          value *= 1e3;
          break;
        case 'm':
          value *= 1e6;
          break;
        case 'b':
          value *= 1e9;
          break;
        case 't':
          value *= 1e12;
          break;
        default:
          break;
      }
      return value;
    }

    return null;
  };

  const applyAmountFilter = () => {
    let minAmount = parseAmount(amountFromInput);
    let maxAmount = parseAmount(amountToInput);

    if (!minAmount && maxAmount) {
      minAmount = amountRange.min;
    }

    if (minAmount && !maxAmount) {
      maxAmount = amountRange.max;
    }

    if (!minAmount && !maxAmount) {
      onAmountChange(null, null);
      return;
    }

    if (minAmount && maxAmount && minAmount > maxAmount) {
      const temp = minAmount;
      minAmount = maxAmount;
      maxAmount = temp;
    }

    onAmountChange(minAmount, maxAmount);
  };

  // ✅ Function to reset values
  const resetAmountFilter = () => {
    setAmountFromInput('');
    setAmountToInput('');
    onAmountChange(null, null);
  };

  const formatInputAmount = (amount) => {
    if (!amount) return '';
    if (amount >= 1e12) {
      return (amount / 1e12).toString() + 't';
    } else if (amount >= 1e9) {
      return (amount / 1e9).toString() + 'b';
    } else if (amount >= 1e6) {
      return (amount / 1e6).toString() + 'm';
    } else if (amount >= 1e3) {
      return (amount / 1e3).toString() + 'k';
    } else {
      return amount.toString();
    }
  };

  useEffect(() => {
    setAmountFromInput(
      selectedAmountFrom !== null && selectedAmountFrom !== undefined
        ? formatInputAmount(selectedAmountFrom)
        : ''
    );
  }, [selectedAmountFrom]);

  useEffect(() => {
    setAmountToInput(
      selectedAmountTo !== null && selectedAmountTo !== undefined
        ? formatInputAmount(selectedAmountTo)
        : ''
    );
  }, [selectedAmountTo]);

  const formatInvestmentAmount = (amount) => {
    const formatAmount = (value, suffix) => {
      return Number.isInteger(value) ? value + suffix : value.toFixed(2) + suffix;
    };

    if (amount >= 1_000_000_000_000) {
      return formatAmount(amount / 1_000_000_000_000, 't');
    } else if (amount >= 1_000_000_000) {
      return formatAmount(amount / 1_000_000_000, 'b');
    } else if (amount >= 1_000_000) {
      return formatAmount(amount / 1_000_000, 'm');
    } else if (amount >= 1_000) {
      return formatAmount(amount / 1_000, 'k');
    }
    return amount.toString();
  };

  if (loading) return <p>Loading...</p>;
  if (error) return <p>Error: {error}</p>;

  return (
    <div className="mt-5">
      <div className="flex items-center gap-x-3 mb-2">
        <label className="text-sm font-medium">{t('app:deals.t4')}</label>

        {/* ✅ Reset button is shown only when a value is entered */}
        {(!!amountFromInput || !!amountToInput) && (
          <Button
            variant="link"
            size="xs"
            onClick={resetAmountFilter}
            className="text-xs py-1 px-2 text-red-500 hover:text-red-700"
            title="Reset range"
          >
            {t('app:other.t1')}
          </Button>
        )}
      </div>

      <div className="flex space-x-2">
        <InputNumber
          className="w-16"
          placeholder="From"
          value={amountFromInput}
          onChange={(val) => setAmountFromInput(val)}
          allowNegative={false}
        />

        <InputNumber
          className="w-16"
          placeholder="To"
          value={amountToInput}
          onChange={(val) => setAmountToInput(val)}
          allowNegative={false}
        />

        <Button variant="outline" size="sm" onClick={applyAmountFilter}>
          OK
        </Button>
      </div>

      <div className="flex text-sm mt-2">
        <span className="w-2/5">Min: ${formatInvestmentAmount(amountRange.min)}</span>
        <span className="w-2/5">Max: ${formatInvestmentAmount(amountRange.max)}</span>
      </div>
    </div>
  );
};

export default AmountRange;
